// Dead-code elimination over the Core IR.
//
// Monomorphize instantiates *generic* functions on demand (one CoreFunc per
// concrete type-argument tuple reached from a call site), so a generic that is
// never called never enters Core. But it lowers every *non-generic* free function
// and impl/trait-default method eagerly — the whole stdlib is in scope, so an
// unused `Logger`/`BufReader`/TCP method is monomorphized right alongside the two
// functions a bank example actually calls. This pass drops that: a backend over
// Core (and the `--emit-core` inspection view) should carry only what `main` can
// reach, not the parts of the prelude a program never touches.
//
// Placement — *after* monomorphization, not before. Core is fully resolved: every
// call is an explicit `staticCall`/`go`/`funcRef` target, every dynamic dispatch a
// `dynCall`/dynamic iterator driver naming a `(trait, method)` whose Core dispatch
// table already lists the concrete implementors. So reachability is an exact graph
// walk over the IR, with no re-derivation of what an operator/trait resolves to (an
// AST-level pass would have to redo that, and the reference graph is over the
// *abstract* program — fuzzy about which impls a trait-typed value can reach). The
// pass is a leaf: it imports only `core.ts` and hands back a `CoreProgram`, so it
// slots in wherever Core is produced for a backend (see `emitCore` / `generateGo`).
//
// What it prunes: unreachable `funcs`, `dispatch` tables no live `dynCall`/dynamic
// loop needs, the `structs`/`enums` no surviving type mentions, and the
// `iterAdapters` (native cursor `iter`/`next`) for containers no surviving type or
// reachable expression mentions. An adapter is reached along paths Core doesn't
// spell out as func ids (a struct's empty container *field*, an `Iterable.iter`
// dispatch reading `collRec.iterFn`), so its liveness is decided at the granularity
// of the *container instantiation* (`Dict<string, JSONValue>`), tracked in
// `liveContainerInsts`: keep the adapter exactly when some live type or reachable
// code names its container. That matters because monomorphize lowers the whole
// prelude eagerly — the JSON helpers construct `Dict<*, JSONValue>` cursors even in
// a program that never calls them — so keeping every adapter wholesale would drag
// each such cursor, and the `JSONValue` graph behind it, into every program. A kept
// adapter's referenced instances (cursor/`Option`/`Pair` structs) are seeded as
// type roots.

import {
  CoreArm,
  CoreDispatch,
  CoreExpr,
  CoreFunc,
  CoreIter,
  CoreLValue,
  CoreProgram,
  CoreStmt,
  FuncId,
  IterAdapter,
  IterDriver,
} from "./core.js";
import { Type } from "../frontend/types.js";

/** Remove everything in `program` that `main` cannot reach. Semantics-preserving:
 *  it only drops declarations no live code refers to, so a backend lowers the same
 *  program with less of the prelude. (The interpreter walks the AST, not Core, so
 *  this affects only the Core-consuming path — the Go backend and `emitCore`.) */
export function eliminateDeadCode(program: CoreProgram): CoreProgram {
  const walker = new Reachability(program);
  walker.run();
  return walker.prune();
}

class Reachability {
  private readonly funcById = new Map<FuncId, CoreFunc>();
  private readonly dispatchByKey = new Map<string, CoreDispatch>();

  /** Ids of functions reachable from `main` (grows as bodies are walked). */
  private readonly reachableFuncs = new Set<FuncId>();
  /** `trait#method` dispatch tables a live `dynCall`/dynamic loop needs. */
  private readonly usedDispatch = new Set<string>();
  /** Struct/enum names some surviving type mentions (closed transitively). */
  private readonly liveTypes = new Set<string>();
  /** Built-in containers (`Set`/`Dict`) a surviving type mentions — tracked apart
   *  from `liveTypes` (they aren't emitted decls) so a container's user `Show` impl
   *  (`Set_show`) is kept when the container is both live and printed. */
  private readonly liveContainers = new Set<string>();
  /** Container *instantiations* (`Dict<string, JSONValue>`) a surviving type or
   *  reachable expression mentions, keyed by `containerKey`. Finer than
   *  `liveContainers` (which forgets the arguments): it decides which iterAdapters
   *  to keep — an adapter is native cursor code for one exact instantiation, and one
   *  no live code constructs or iterates is dead. Without this, a container built
   *  only inside an eagerly-lowered but unreachable prelude function (e.g. the JSON
   *  helpers' `Dict<*, JSONValue>`) would keep its cursor — and, through it, the
   *  whole `JSONValue` type graph — alive in every program. */
  private readonly liveContainerInsts = new Set<string>();
  /** iterAdapters seeded as type roots (their instantiation went live). Also the
   *  set `prune` keeps: an unseeded adapter is emitted by no reachable path. */
  private readonly liveAdapters = new Set<IterAdapter>();
  /** Whether reachable code calls a `Show`-dispatching builtin (`print`/`str`). Both
   *  render any value through `__show`, which forwards to a `Type_show` method with
   *  no `dynCall` in the IR — so those methods are kept implicitly (see `run`). */
  private showUsed = false;

  private readonly queue: FuncId[] = [];

  constructor(private readonly program: CoreProgram) {
    for (const f of program.funcs) this.funcById.set(f.id, f);
    for (const d of program.dispatch) this.dispatchByKey.set(key(d.trait, d.method), d);
  }

  run() {
    this.enqueue(this.program.mainId);
    this.drain();
    this.closeTypes();
    // `print`/`str` render any live value via its `Show` impl — a `Type_show` the
    // backend reaches through `__show`, not a `dynCall`, so nothing above kept it.
    // Keep every live type's `_show`, then re-drain and re-close to a fixpoint: a
    // show body can reach new funcs, and those can make new types live (whose own
    // `_show` is then needed). Only *live* types get one, so a printed program never
    // resurrects an unused type's `Show` (a bank prints accounts, not `LogLevel`).
    if (this.showUsed) {
      let changed = true;
      while (changed) {
        const addedShow = this.enqueueLiveShowMethods();
        this.drain();
        const beforeTypes = this.liveTypes.size;
        this.closeTypes();
        changed = addedShow || this.liveTypes.size !== beforeTypes;
      }
    }
  }

  /** Walk every queued function until the worklist is empty. */
  private drain() {
    while (this.queue.length) this.walkFunc(this.funcById.get(this.queue.pop()!)!);
  }

  /** Enqueue the `_show` method of every live type/container that has one and isn't
   *  reachable yet. Returns whether it enqueued anything. */
  private enqueueLiveShowMethods(): boolean {
    let added = false;
    const keep = (name: string) => {
      const id = `${name}_show` as FuncId;
      if (this.funcById.has(id) && !this.reachableFuncs.has(id)) {
        this.enqueue(id);
        added = true;
      }
    };
    for (const name of this.liveTypes) keep(name);
    for (const c of this.liveContainers) keep(c);
    return added;
  }

  /** The pruned program: only reachable funcs, needed dispatch tables, and the
   *  structs/enums live types mention. `iterAdapters`, `memory`, and `mainId` pass
   *  through unchanged (see the module note on why adapters are kept wholesale). */
  prune(): CoreProgram {
    return {
      structs: this.program.structs.filter((s) => this.liveTypes.has(s.name)),
      enums: this.program.enums.filter((e) => this.liveTypes.has(e.name)),
      funcs: this.program.funcs.filter((f) => this.reachableFuncs.has(f.id)),
      mainId: this.program.mainId,
      memory: this.program.memory,
      dispatch: this.program.dispatch
        .filter((d) => this.usedDispatch.has(key(d.trait, d.method)))
        .map((d) => ({ ...d, cases: d.cases.filter((c) => this.caseTypeLive(c.type)) })),
      iterAdapters: this.program.iterAdapters.filter((a) => this.liveAdapters.has(a)),
    };
  }

  /** Whether a dispatch case's receiver type survives — so a case forwarding to a
   *  never-constructed implementor is dropped from the table (and no longer listed
   *  as an implementor in the emitted Go). Kept in step with iterAdapter pruning: an
   *  iterator cursor whose container instantiation is dead loses both its adapter and
   *  its `Iterator.next` case together, so the table never names a cursor the backend
   *  no longer emits. */
  private caseTypeLive(t: Type): boolean {
    if (t.k === "struct" || t.k === "enum") return this.liveTypes.has(t.name);
    if (t.k === "generic" && (t.name === "Set" || t.name === "Dict" || t.name === "List")) {
      // The erased builtin case (`Set<unknown> => Set_iter`) stands for every
      // instantiation, so keep it whenever any instantiation of this container is
      // live; a concrete-typed case keys on its exact instantiation.
      if (t.args.every((a) => a.k === "unknown")) return this.liveContainers.has(t.name);
      return this.liveContainerInsts.has(containerKey(t.name, t.args));
    }
    return true;
  }

  private enqueue(id: FuncId) {
    if (this.funcById.has(id) && !this.reachableFuncs.has(id)) {
      this.reachableFuncs.add(id);
      this.queue.push(id);
    }
  }

  /** Mark a `(trait, method)` dispatch as live: keep its table and make every
   *  concrete implementor it can forward to reachable (each may in turn reach more
   *  dispatches — handled by the BFS, since a case target is walked when dequeued). */
  private useDispatch(trait: string, method: string) {
    const k = key(trait, method);
    if (this.usedDispatch.has(k)) return;
    this.usedDispatch.add(k);
    const d = this.dispatchByKey.get(k);
    if (d) for (const c of d.cases) this.enqueue(c.target);
  }

  private walkFunc(fn: CoreFunc) {
    if (fn.self) this.type(fn.self.type);
    for (const p of fn.params) this.type(p.type);
    this.type(fn.ret);
    for (const s of fn.body) this.stmt(s);
  }

  // -- statements ---------------------------------------------------------

  private stmt(s: CoreStmt) {
    switch (s.s) {
      case "let":
        this.type(s.type);
        this.expr(s.value);
        return;
      case "assign":
        this.lvalue(s.target);
        this.expr(s.value);
        return;
      case "expr":
        this.expr(s.expr);
        return;
      case "return":
        if (s.value) this.expr(s.value);
        return;
      case "if":
        this.expr(s.cond);
        s.then.forEach((x) => this.stmt(x));
        s.else?.forEach((x) => this.stmt(x));
        return;
      case "while":
        this.expr(s.cond);
        s.body.forEach((x) => this.stmt(x));
        return;
      case "for":
        this.type(s.varType);
        this.iter(s.iter);
        s.body.forEach((x) => this.stmt(x));
        return;
      case "match":
        this.expr(s.subject);
        this.liveTypes.add(s.enumName);
        s.arms.forEach((a) => this.arm(a));
        return;
      case "go":
        this.enqueue(s.target);
        s.args.forEach((a) => this.expr(a));
        return;
      case "txn":
        s.body.forEach((x) => this.stmt(x));
        return;
      case "retry":
        return; // no operands, no callees
      case "block":
        s.body.forEach((x) => this.stmt(x));
        return;
    }
  }

  private lvalue(lv: CoreLValue) {
    this.type(lv.type);
    if (lv.lv === "field") this.expr(lv.object);
  }

  private arm(a: CoreArm) {
    for (const b of a.bindings) this.type(b.type);
    a.body.forEach((x) => this.stmt(x));
  }

  private iter(it: CoreIter) {
    this.expr(it.value);
    this.type(it.elem);
    switch (it.it) {
      case "collection":
        return;
      case "iterator":
        this.liveTypes.add(it.option);
        this.driver(it.next);
        return;
      case "iterable":
        this.liveTypes.add(it.option);
        this.driver(it.iter);
        this.driver(it.next);
        return;
    }
  }

  private driver(d: IterDriver) {
    if (d.via === "static") this.enqueue(d.target);
    else this.useDispatch(d.trait, d.method);
  }

  // -- expressions --------------------------------------------------------

  private expr(x: CoreExpr) {
    this.type(x.type);
    switch (x.e) {
      case "int":
      case "float":
      case "string":
      case "char":
      case "bool":
      case "local":
        return;
      case "field":
        this.expr(x.object);
        return;
      case "unary":
        this.expr(x.operand);
        return;
      case "binop":
        this.expr(x.left);
        this.expr(x.right);
        return;
      case "if":
        this.expr(x.cond);
        this.expr(x.then);
        this.expr(x.else);
        return;
      case "staticCall":
        this.enqueue(x.target);
        x.args.forEach((a) => this.expr(a));
        return;
      case "dynCall":
        this.useDispatch(x.trait, x.method);
        this.expr(x.recv);
        x.args.forEach((a) => this.expr(a));
        return;
      case "funcRef":
        this.enqueue(x.target);
        return;
      case "closure":
        for (const p of x.params) this.type(p.type);
        this.type(x.ret);
        for (const c of x.captures) this.type(c.type);
        x.body.forEach((s) => this.stmt(s));
        return;
      case "callValue":
        this.expr(x.callee);
        x.args.forEach((a) => this.expr(a));
        return;
      case "builtin":
        if (x.name === "print" || x.name === "str") this.showUsed = true;
        x.args.forEach((a) => this.expr(a));
        return;
      case "construct":
        this.liveTypes.add(x.typeName);
        x.fields.forEach((f) => this.expr(f.value));
        return;
      case "enumConstruct":
        this.liveTypes.add(x.enumName);
        x.fields.forEach((f) => this.expr(f.value));
        return;
      case "collectionNew":
        // The cursor/`Option`/`Pair` this container's `iterFn` builds live on its
        // (kept) iterAdapter, seeded as roots in `closeTypes`; nothing to add here.
        return;
      case "collectionLit":
        // Same as `collectionNew` for the container itself — but the entries are
        // ordinary expressions, and whatever they construct or call is live.
        x.entries.forEach((en) => {
          if (en.key) this.expr(en.key);
          this.expr(en.value);
        });
        return;
      case "builtinMethod":
        this.expr(x.recv);
        x.args.forEach((a) => this.expr(a));
        return;
    }
  }

  // -- types --------------------------------------------------------------

  /** Record every struct/enum name a type mentions. Generic containers (`Set`/`Dict`)
   *  and traits aren't emitted decls, but their arguments may be — so recurse. */
  private type(t: Type) {
    switch (t.k) {
      case "struct":
      case "enum":
        this.liveTypes.add(t.name);
        return;
      case "struct-generic":
      case "enum-generic":
        // Core flattens these to `struct`/`enum` with mangled names, so this is
        // defensive: keep the head name and recurse the arguments.
        this.liveTypes.add(t.name);
        t.args.forEach((a) => this.type(a));
        return;
      case "generic":
        if (t.name === "Set" || t.name === "Dict" || t.name === "List") {
          this.liveContainers.add(t.name);
          this.liveContainerInsts.add(containerKey(t.name, t.args));
        }
        t.args.forEach((a) => this.type(a));
        return;
      case "trait":
        t.args.forEach((a) => this.type(a));
        return;
      case "fn":
        t.params.forEach((p) => this.type(p));
        this.type(t.ret);
        return;
      default:
        return; // scalar / void / unknown / var / columns — no decl to keep
    }
  }

  /** Close `liveTypes` over declaration structure and the *live* iterAdapters. A live
   *  struct's field types, and a live enum's variant field types, pull in the decls
   *  they mention; an iterAdapter is seeded (its cursor, `Option`, and `Pair`
   *  instances made roots) once its container instantiation goes live — which its own
   *  seeded cursor field, or a live enum's container field, can trigger, so the two
   *  interleave in one fixpoint. Iterate until neither grows. */
  private closeTypes() {
    const structByName = new Map(this.program.structs.map((s) => [s.name, s]));
    const enumByName = new Map(this.program.enums.map((e) => [e.name, e]));
    let changed = true;
    while (changed) {
      const beforeTypes = this.liveTypes.size;
      const beforeAdapters = this.liveAdapters.size;
      // Seed any adapter whose container instantiation is now live (and re-record
      // that instantiation so a nested container in `elem`/`value` is picked up).
      for (const a of this.program.iterAdapters) {
        if (this.liveAdapters.has(a)) continue;
        if (this.liveContainerInsts.has(containerKey(a.container, adapterArgs(a)))) {
          this.liveAdapters.add(a);
          this.seedAdapter(a);
        }
      }
      for (const name of [...this.liveTypes]) {
        const s = structByName.get(name);
        if (s) for (const f of s.fields) this.type(f.type);
        const e = enumByName.get(name);
        if (e) for (const v of e.variants) for (const f of v.fields) this.type(f.type);
      }
      changed = this.liveTypes.size !== beforeTypes || this.liveAdapters.size !== beforeAdapters;
    }
  }

  /** Seed an iterAdapter's constructed instances as live types. */
  private seedAdapter(a: IterAdapter) {
    this.liveTypes.add(a.struct);
    this.liveTypes.add(a.option);
    if (a.pair) this.liveTypes.add(a.pair);
    this.type(a.elem);
    if (a.value) this.type(a.value);
  }
}

function key(trait: string, method: string): string {
  return `${trait}#${method}`;
}

/** The type arguments of an iterAdapter's container instantiation: `[elem]` for a
 *  `Set`, `[elem, value]` for a `Dict` — the same arg list `type()` sees on the
 *  `generic` container type, so their `containerKey`s match. */
function adapterArgs(a: IterAdapter): Type[] {
  return a.value ? [a.elem, a.value] : [a.elem];
}

/** A stable key for one container instantiation (`Dict<string, JSONValue>`), used to
 *  match a live container type against the adapter that iterates it. */
function containerKey(container: string, args: Type[]): string {
  return `${container}<${args.map(typeKey).join(",")}>`;
}

/** A structural key for a type — enough to tell two container instantiations apart
 *  by their arguments. Core has flattened generics to mangled `struct`/`enum` names,
 *  so the head name captures the instantiation; the recursive arms stay defensive. */
function typeKey(t: Type): string {
  switch (t.k) {
    case "struct":
    case "enum":
      return t.name;
    case "struct-generic":
    case "enum-generic":
    case "generic":
    case "trait":
      return `${t.name}<${t.args.map(typeKey).join(",")}>`;
    case "fn":
      return `fn(${t.params.map(typeKey).join(",")})->${typeKey(t.ret)}`;
    default:
      return t.k;
  }
}
